import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import InputWithLabel from '../../components/form/input/InputWithLabel';
import SelectWithLabel from '../../components/form/input/SelectWithLabel';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import AdminFormActions from '../../components/admin/AdminFormActions';
import AdminFormSection from '../../components/admin/AdminFormSection';
import AdminPageShell from '../../components/admin/AdminPageShell';
import { adminAlertErrorClass, adminFormFullWidthClass, adminSpinnerClass } from '../../components/admin/adminStyles';

const initialFormData = {
  fullName: '',
  nationalInsuranceNumber: '',
  vatNumber: '',
  address: '',
  postCode: '',
  licensingAuthority: '',
  mobileNumber: '',
  emergencyContact: '',
  communicationEmail: '',
  bookingEmail: '',
  workFrom: '',
  workUpTo: '',
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
  adminUser: '',
  adminPassword: '',
  accountName: '',
  accountNumber: '',
  sortCode: '',
  bankName: '',
  bankAddress: '',
  isAvailable: true,
  subcompanyId: '' as string | null,
};

interface SubcompanyOption {
  id: string;
  name: string;
}

const WEEKDAYS = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
] as const;

export default function DriverForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState(initialFormData);
  const [subcompanies, setSubcompanies] = useState<SubcompanyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubcompanies();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchDriverData();
    }
  }, [id]);

  const fetchSubcompanies = async () => {
    try {
      const response = await axiosInstance.get<SubcompanyOption[]>('/drivers/subcompanies');
      setSubcompanies(response.data);
    } catch (fetchError) {
      console.error('Error fetching subcompanies:', fetchError);
    }
  };

  const fetchDriverData = async () => {
    setFetching(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/drivers/${id}`);
      setFormData({
        ...initialFormData,
        ...response.data,
        adminPassword: '',
        subcompanyId: response.data.subcompanyId ?? '',
      });
    } catch (fetchError) {
      setError('Failed to fetch driver data');
      console.error('Error fetching driver:', fetchError);
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const buildPayload = (data: typeof formData) => {
        const { subcompany, ...rest } = data as typeof formData & { subcompany?: unknown };
        const payload = {
          ...rest,
          subcompanyId: rest.subcompanyId?.trim() ? rest.subcompanyId.trim() : null,
        };
        if (isEditMode && !payload.adminPassword) {
          delete (payload as { adminPassword?: string }).adminPassword;
        }
        return payload;
      };

      if (isEditMode) {
        await axiosInstance.put(`/drivers/${id}`, buildPayload(formData));
      } else {
        await axiosInstance.post('/drivers', buildPayload(formData));
      }
      navigate('/drivers');
    } catch (submitError: unknown) {
      const axiosError = submitError as { response?: { data?: { message?: string | string[] } } };
      const message = axiosError.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join(', ')
          : message || 'Failed to save driver'
      );
      console.error('Error saving driver:', submitError);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleDay = (day: (typeof WEEKDAYS)[number]['key']) => {
    setFormData(prev => ({ ...prev, [day]: !prev[day] }));
  };

  if (fetching) {
    return (
      <AdminPageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className={adminSpinnerClass} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle={isEditMode ? 'Edit Driver' : 'Add Driver'}
        description={
          isEditMode
            ? 'Update driver profile, schedule, and banking details'
            : 'Register a new driver for your fleet'
        }
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && <div className={`mb-4 ${adminAlertErrorClass}`}>{error}</div>}

        <form onSubmit={handleSubmit} className="w-full space-y-8">
          <AdminFormSection title="Personal information">
            <InputWithLabel
              label="Full name"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
              placeholder="e.g. Tom Williams"
            />
            <InputWithLabel
              label="National Insurance number"
              name="nationalInsuranceNumber"
              value={formData.nationalInsuranceNumber}
              onChange={handleInputChange}
              placeholder="e.g. AB123456C"
            />
            <InputWithLabel
              label="VAT number"
              name="vatNumber"
              value={formData.vatNumber}
              onChange={handleInputChange}
              placeholder="e.g. GB123456789"
            />
            <InputWithLabel
              label="Licensing authority"
              name="licensingAuthority"
              value={formData.licensingAuthority}
              onChange={handleInputChange}
              placeholder="e.g. TfL"
            />
            <div className={adminFormFullWidthClass}>
              <InputWithLabel
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Street, city"
              />
            </div>
            <InputWithLabel
              label="Post code"
              name="postCode"
              value={formData.postCode}
              onChange={handleInputChange}
              placeholder="e.g. TW6 1EW"
            />
          </AdminFormSection>

          <AdminFormSection title="Contact">
            <InputWithLabel
              label="Mobile number"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleInputChange}
              required
              placeholder="+44 7700 900000"
            />
            <InputWithLabel
              label="Emergency contact"
              name="emergencyContact"
              value={formData.emergencyContact}
              onChange={handleInputChange}
              placeholder="+44 7700 900001"
            />
            <InputWithLabel
              label="Communication email"
              name="communicationEmail"
              type="email"
              value={formData.communicationEmail}
              onChange={handleInputChange}
              required
              placeholder="driver@example.com"
            />
            <InputWithLabel
              label="Booking email"
              name="bookingEmail"
              type="email"
              value={formData.bookingEmail}
              onChange={handleInputChange}
              placeholder="bookings@example.com"
            />
          </AdminFormSection>

          <AdminFormSection title="Portal access" columns="2">
            <InputWithLabel
              label="Admin username"
              name="adminUser"
              value={formData.adminUser}
              onChange={handleInputChange}
              required
              placeholder="Driver login username"
            />
            <InputWithLabel
              label={isEditMode ? 'New password (optional)' : 'Admin password'}
              name="adminPassword"
              type="password"
              value={formData.adminPassword}
              onChange={handleInputChange}
              required={!isEditMode}
              placeholder={isEditMode ? 'Leave blank to keep current' : 'Minimum 6 characters'}
            />
          </AdminFormSection>

          <AdminFormSection title="Banking details">
            <InputWithLabel
              label="Account name"
              name="accountName"
              value={formData.accountName}
              onChange={handleInputChange}
              required
            />
            <InputWithLabel
              label="Account number"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleInputChange}
              required
            />
            <InputWithLabel
              label="Sort code"
              name="sortCode"
              value={formData.sortCode}
              onChange={handleInputChange}
              required
              placeholder="00-00-00"
            />
            <InputWithLabel
              label="Bank name"
              name="bankName"
              value={formData.bankName}
              onChange={handleInputChange}
              required
            />
            <div className={adminFormFullWidthClass}>
              <InputWithLabel
                label="Bank address"
                name="bankAddress"
                value={formData.bankAddress}
                onChange={handleInputChange}
                required
              />
            </div>
          </AdminFormSection>

          <AdminFormSection title="Work schedule & company">
            <SelectWithLabel
              label="Company"
              name="subcompanyId"
              value={formData.subcompanyId ?? ''}
              onChange={handleInputChange}
              hint="Choose parent company when the driver is employed directly by you."
            >
              <option value="">Parent company (direct)</option>
              {subcompanies.map((subcompany) => (
                <option key={subcompany.id} value={subcompany.id}>
                  {subcompany.name}
                </option>
              ))}
            </SelectWithLabel>
            <InputWithLabel
              label="Work from"
              name="workFrom"
              type="time"
              value={formData.workFrom}
              onChange={handleInputChange}
            />
            <InputWithLabel
              label="Work until"
              name="workUpTo"
              type="time"
              value={formData.workUpTo}
              onChange={handleInputChange}
            />
            <div className={adminFormFullWidthClass}>
              <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                Working days
              </p>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(({ key, label }) => {
                  const active = formData[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleDay(key)}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600'
                          : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200 hover:bg-gray-200 dark:bg-white/5 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-white/10'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <label
              htmlFor="isAvailable"
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                formData.isAvailable
                  ? 'border-brand-200 bg-brand-50 dark:border-brand-500/30 dark:bg-brand-500/10'
                  : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-white/[0.02]'
              }`}
            >
              <input
                type="checkbox"
                name="isAvailable"
                checked={formData.isAvailable}
                onChange={handleInputChange}
                id="isAvailable"
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20"
              />
              <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                Available for bookings
              </span>
            </label>
          </AdminFormSection>

          <AdminFormActions listRoute="/drivers" saving={loading} saveLabel="Save driver" />
        </form>
      </div>
    </AdminPageShell>
  );
}
